import React, { useState, lazy, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { allArticles } from '../Data/articles';
import { applyPersonaTheme, resetPersonaTheme, PersonaTheme } from '../../utils/personaTheme';
import { PageIntro, Feedback } from '../../design/ui';
import { ArrowLeft } from '../../design/icons';
import { useReducedMotion } from '../../animations';
import { ArticleReaderNav } from './ArticleReaderNav';
import { estimateReadingMinutes } from './articleReading';
import { articleReturnPath, withArticleReturn } from './articleNavigation';
import AgentSystemFilm from '../Media/AgentSystemFilm';
import MotionStudy from '../Media/MotionStudy';
import './articles.css';
import './article-reader.css';

// Lazy-load demos — TF.js only loads when the article needs it
const DEMOS: Record<string, React.LazyExoticComponent<React.FC>> = {
    'image-classifier': lazy(() => import('../Demos/ImageClassifier').then(m => ({ default: m.ImageClassifier }))),
    'semantic-search': lazy(() => import('../Demos/SemanticSearch').then(m => ({ default: m.SemanticSearch }))),
    'extractive-qa': lazy(() => import('../Demos/ExtractiveQA').then(m => ({ default: m.ExtractiveQA }))),
    'ai-ecosystem': lazy(() => import('../Demos/AIEcosystemDemo').then(m => ({ default: m.AIEcosystemDemo }))),
    'docker-simulator': lazy(() => import('../Demos/DockerSimulator').then(m => ({ default: m.DockerSimulator }))),
    'vector-calc': lazy(() => import('../Demos/VectorCalcLab').then(m => ({ default: m.VectorCalcLab }))),
    'format-tree': lazy(() => import('../Demos/InteractiveFormatTree').then(m => ({ default: m.InteractiveFormatTree }))),
    'rag-pipeline': lazy(() => import('../Demos/RAGPipelineDemo').then(m => ({ default: m.RAGPipelineDemo }))),
    'pagerank-graph': lazy(() => import('../Demos/PageRankGraph').then(m => ({ default: m.PageRankGraph }))),
    'on-device-calc': lazy(() => import('../Demos/OnDeviceCalculator').then(m => ({ default: m.OnDeviceCalculator }))),
};

// ─── Persona definitions ───────────────────────────────────────────────────
interface PersonaDef {
    id: string;
    icon: string;
    label: string;
    description: string;
    theme: PersonaTheme;
}

const PERSONAS: PersonaDef[] = [
    {
        id: 'terminal',
        icon: '>_',
        label: 'Terminal',
        description: 'Developer',
        theme: {
            name: 'Terminal',
            primary: '#50fa7b',
            secondary: '#bd93f9',
            bg: '#0d0f18',
            surface: '#161824',
            text: '#f8f8f2',
            muted: '#8995ba',
            border: 'rgba(80,250,123,0.2)',
            font: 'JetBrains Mono',
            borderRadius: '0',
            bgPattern: 'scanlines',
            chatIcon: '👾',
            heroText: 'Built with intention. Shipped with precision.',
            customCSS: `
                article, .article-card { border: 1px solid rgba(80,250,123,0.25) !important; border-radius: 4px !important; }
                h1, h2, h3 { font-family: 'JetBrains Mono', monospace !important; font-style: normal !important; }
                button { border-radius: 4px !important; }
                a { color: #50fa7b !important; }
            `,
        },
    },
    {
        id: 'enterprise',
        icon: '📊',
        label: 'Enterprise',
        description: 'Executive',
        theme: {
            name: 'Enterprise',
            primary: '#0a1628',
            secondary: '#4f6af0',
            bg: '#f8f9fc',
            surface: '#ffffff',
            text: '#0a1628',
            muted: '#64748b',
            border: '#e2e8f0',
            font: 'Libre Baskerville',
            borderRadius: '0',
            bgPattern: 'solid',
            chatIcon: '💼',
            heroText: 'Strategy. Architecture. Execution.',
            customCSS: `
                h1, h2, h3 { font-family: 'Libre Baskerville', serif !important; color: #0a1628 !important; }
                article, .article-card { box-shadow: 0 1px 4px rgba(10,22,40,0.08) !important; border-radius: 6px !important; }
                code { background: #f1f5f9 !important; color: #0a1628 !important; }
                pre { background: #f1f5f9 !important; color: #0a1628 !important; border-left: 3px solid #4f6af0; }
            `,
        },
    },
    {
        id: 'genz',
        icon: '✦',
        label: 'Gen Z',
        description: 'Creative',
        theme: {
            name: 'Gen Z',
            primary: '#ff006e',
            secondary: '#fb5607',
            bg: '#0d0014',
            surface: '#1a0020',
            text: '#fff0f8',
            muted: '#cc80aa',
            border: 'rgba(255,0,110,0.3)',
            font: 'Space Grotesk',
            borderRadius: '0',
            bgPattern: 'dots',
            chatIcon: '⚡',
            heroText: 'Building things that break the mould.',
            customCSS: `
                h1, h2, h3 { font-family: 'Space Grotesk', sans-serif !important; font-style: normal !important; letter-spacing: -0.03em !important; }
                h2 { font-size: 28px !important; }
                article, .article-card { border-radius: 20px !important; border: 1px solid rgba(255,0,110,0.3) !important; }
                button { border-radius: 20px !important; }
                strong { color: #ff006e !important; }
                pre { border-radius: 16px !important; border: 1px solid rgba(255,0,110,0.2) !important; }
            `,
        },
    },
    {
        id: 'researcher',
        icon: '📚',
        label: 'Researcher',
        description: 'Academic',
        theme: {
            name: 'Researcher',
            primary: '#5c4a2a',
            secondary: '#8b6914',
            bg: '#faf7f0',
            surface: '#f5f0e8',
            text: '#2c1f0e',
            muted: '#7a6652',
            border: '#d4c4a0',
            font: 'Playfair Display',
            borderRadius: '0',
            bgPattern: 'solid',
            chatIcon: '🧠',
            heroText: 'Rigour, curiosity, and the long view.',
            customCSS: `
                h1, h2, h3 { font-family: 'Playfair Display', serif !important; font-style: italic !important; }
                body { line-height: 1.9 !important; }
                pre { font-size: 13px !important; background: #ede8dc !important; color: #2c1f0e !important; border-left: 3px solid #8b6914 !important; border-radius: 0 !important; }
                code { background: #ede8dc !important; color: #5c4a2a !important; }
                blockquote { border-left: 3px solid #8b6914; padding-left: 16px; font-style: italic; color: #7a6652; }
            `,
        },
    },
    {
        id: 'explorer',
        icon: '🌍',
        label: 'Adventurer',
        description: 'Explorer',
        theme: {
            name: 'Adventurer',
            primary: '#c17b3e',
            secondary: '#4a7c59',
            bg: '#1a1208',
            surface: '#2a1e0e',
            text: '#f0e6d0',
            muted: '#a08c6e',
            border: 'rgba(193,123,62,0.3)',
            font: 'Syne',
            borderRadius: '0',
            bgPattern: 'grid',
            chatIcon: '🌍',
            heroText: 'Every system is a new territory to map.',
            customCSS: `
                h1, h2, h3 { font-family: 'Syne', sans-serif !important; font-style: normal !important; letter-spacing: -0.01em !important; }
                article, .article-card { border: 1px solid rgba(193,123,62,0.25) !important; }
                strong { color: #c17b3e !important; }
                pre { background: #120e04 !important; border-left: 3px solid #4a7c59 !important; border-radius: 4px !important; }
                code { background: rgba(193,123,62,0.15) !important; color: #c17b3e !important; }
            `,
        },
    },
];

// ─── Persona preview cards ─────────────────────────────────────────────────
const PersonaCards: React.FC<{ onApply: (theme: PersonaTheme, name: string) => void; activeId: string | null }> = ({ onApply, activeId }) => {
    const [hovered, setHovered] = useState<string | null>(null);

    return (
        <div style={{ margin: '32px 0 8px' }}>
            <div className="persona-cards-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: '12px',
            }}>
                {PERSONAS.map(p => {
                    const isActive = activeId === p.id;
                    const isHovered = hovered === p.id;
                    const bg = p.theme.bg || '#0d0f18';
                    const surface = p.theme.surface || '#161824';
                    const primary = p.theme.primary || '#50fa7b';
                    const text = p.theme.text || '#f8f8f2';
                    const muted = p.theme.muted || '#8995ba';

                    return (
                        <button
                            type="button"
                            className="article-persona-choice tap-hit"
                            aria-pressed={isActive}
                            key={p.id}
                            onClick={() => onApply(p.theme, p.id)}
                            onMouseEnter={() => setHovered(p.id)}
                            onMouseLeave={() => setHovered(null)}
                            style={{
                                all: 'unset',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: '0',
                                overflow: 'hidden',
                                border: isActive
                                    ? `2px solid ${primary}`
                                    : `1.5px solid ${isHovered ? primary + '88' : primary + '33'}`,
                                transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
                                boxShadow: isHovered
                                    ? `0 8px 24px ${primary}44`
                                    : isActive ? `0 4px 16px ${primary}55` : '0 2px 8px rgba(0,0,0,0.2)',
                                transition: 'all 0.2s ease',
                                background: bg,
                            }}
                        >
                            {/* Mini chrome bar */}
                            <div style={{
                                background: surface,
                                padding: '5px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                borderBottom: `1px solid ${primary}22`,
                            }}>
                                {['#ff5f57','#ffbd2e','#28c840'].map((c, i) => (
                                    <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: c, display: 'inline-block' }} />
                                ))}
                            </div>

                            {/* Mini content preview */}
                            <div style={{ padding: '12px 10px 14px', flex: 1 }}>
                                <div style={{
                                    fontSize: '20px',
                                    marginBottom: '6px',
                                    fontFamily: p.id === 'terminal' ? 'monospace' : 'inherit',
                                    color: primary,
                                    lineHeight: 1,
                                }}>{p.icon}</div>

                                <div style={{
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    color: text,
                                    fontFamily: p.id === 'terminal' ? 'monospace' : 'inherit',
                                    marginBottom: '2px',
                                    letterSpacing: p.id === 'terminal' ? '0.05em' : '0',
                                }}>{p.label}</div>

                                <div style={{
                                    fontSize: '10px',
                                    color: muted,
                                    fontFamily: p.id === 'terminal' ? 'monospace' : 'inherit',
                                    marginBottom: '8px',
                                }}>{p.description}</div>

                                {/* Mini colour swatches */}
                                <div style={{ display: 'flex', gap: '3px' }}>
                                    {[primary, p.theme.secondary || muted, surface].map((c, i) => (
                                        <span key={i} style={{
                                            width: 10, height: 10, borderRadius: '50%',
                                            background: c, display: 'inline-block',
                                            border: `1px solid ${text}22`,
                                        }} />
                                    ))}
                                </div>
                            </div>

                            {/* Apply label */}
                            <div style={{
                                background: isActive ? primary : `${primary}22`,
                                padding: '5px',
                                textAlign: 'center',
                                fontSize: '9px',
                                fontWeight: '700',
                                letterSpacing: '0.08em',
                                color: isActive ? bg : primary,
                                fontFamily: 'monospace',
                                textTransform: 'uppercase',
                                transition: 'all 0.15s',
                            }}>
                                {isActive ? '✓ active' : 'apply →'}
                            </div>
                        </button>
                    );
                })}
            </div>
            {activeId && (
                <button
                    type="button"
                    className="article-persona-choice tap-hit"
                    onClick={() => onApply({ name: 'reset' } as any, '__reset__')}
                    style={{
                        all: 'unset', cursor: 'pointer',
                        display: 'block', marginTop: '12px',
                        fontSize: '12px', color: 'var(--text-muted, var(--bs-muted))',
                        fontFamily: 'monospace',
                        transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-primary, var(--bs-ink))')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted, var(--bs-muted))')}
                >
                    $ reset-theme
                </button>
            )}
        </div>
    );
};

// ─── Upgraded persona experiment widget ───────────────────────────────────
const PersonaExperiment: React.FC<{ onApply: (theme: PersonaTheme, name: string) => void; activePersonaId: string | null }> = ({ onApply, activePersonaId }) => {
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
    const [appliedTheme, setAppliedTheme] = useState<PersonaTheme | null>(null);
    const mono = { fontFamily: "'JetBrains Mono','Fira Code',monospace" } as React.CSSProperties;

    const applyTheme = async () => {
        if (!description.trim()) return;
        setStatus('loading');
        try {
            const res = await fetch('/api/persona', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ description: description.trim() }),
            });
            const data = await res.json();
            if (!res.ok || !data.theme) throw new Error(data.error || 'failed');
            onApply(data.theme, '__custom__');
            setAppliedTheme(data.theme);
            setStatus('done');
        } catch {
            setStatus('error');
        }
    };

    const reset = () => {
        onApply({ name: 'reset' } as any, '__reset__');
        setStatus('idle');
        setDescription('');
        setAppliedTheme(null);
    };

    return (
        <div className="article-persona-widget" style={{
            background: 'var(--bg-card, var(--bs-raised))', borderRadius: '0', overflow: 'hidden',
            border: '1px solid var(--border, var(--bs-rule))', margin: '40px 0',
        }}>
            {/* Title bar */}
            <div style={{
                background: 'var(--bg, var(--bs-sunken))', padding: '12px 16px',
                borderBottom: '1px solid var(--border, var(--bs-rule))',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ ...mono, color:'var(--text-primary, var(--bs-ink))', fontSize:'12px' }}>
                        Persona experiment
                    </span>
                </div>
                {status === 'done' && appliedTheme && (
                    <span role="status" style={{ ...mono, fontSize: '11px', color: 'var(--text-primary, var(--bs-ink))' }}>
                        ✓ {appliedTheme.name || 'custom'}
                    </span>
                )}
            </div>

            <div style={{ padding: '24px' }}>
                {status !== 'done' ? (
                    <>
                        <div style={{ ...mono, color: 'var(--text-muted, var(--bs-muted))', fontSize: '13px', marginBottom: '16px', lineHeight: 1.6 }}>
                            Describe yourself. The model generates a theme and applies it to the page.
                        </div>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <input
                                aria-label="Describe yourself for a custom theme"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                onKeyDown={e => {
                                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                                    if (e.key === 'Enter') applyTheme();
                                }}
                                placeholder="e.g. I'm a Gen Z dev who loves bold experimental design"
                                disabled={status === 'loading'}
                                style={{
                                    flex: '1 1 240px', minWidth: 0, width: '100%',
                                    background: 'var(--bg, var(--bs-ground))',
                                    border: '1px solid var(--border, var(--bs-rule-strong))',
                                    borderRadius: '0', padding: '10px 14px',
                                    color: 'var(--text-primary, var(--bs-ink))', ...mono, fontSize: '13px',
                                    caretColor: 'var(--persona-primary, var(--bs-accent))',
                                    transition: 'border-color 0.15s',
                                }}
                            />
                            <button
                                type="button"
                                onClick={applyTheme}
                                disabled={!description.trim() || status === 'loading'}
                                style={{
                                    ...mono, fontSize: '13px',
                                    background: 'var(--bs-accent-fill)',
                                    border: '1px solid var(--bs-accent-fill)',
                                    borderRadius: '0', padding: '10px 20px',
                                    color: 'var(--bs-accent-ink)', cursor: status === 'loading' ? 'wait' : 'pointer',
                                    opacity: !description.trim() || status === 'loading' ? 0.6 : 1,
                                    transition: 'all 0.15s',
                                    minWidth: '130px',
                                }}
                            >
                                {status === 'loading' ? (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ animation: 'blink 0.6s step-end infinite' }}>▌</span>
                                        generating...
                                    </span>
                                ) : '$ apply theme'}
                            </button>
                        </div>

                        {/* Suggested prompts */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                            {[
                                "I'm a startup founder who moves fast",
                                "I'm a data scientist who likes clean, dense info",
                                "I'm a designer obsessed with colour and type",
                            ].map(hint => (
                                <button type="button" key={hint} onClick={() => setDescription(hint)} disabled={status === 'loading'} className="persona-hint-btn" style={{
                                    all: 'unset', cursor: 'pointer',
                                    ...mono, fontSize: '11px',
                                    color: 'var(--text-muted, var(--bs-muted))',
                                    background: 'var(--bg, var(--bs-ground))',
                                    border: '1px solid var(--border, var(--bs-rule))',
                                    borderRadius: '0',
                                    padding: '10px', minHeight: 44,
                                    transition: 'all 0.1s',
                                }}
                                >{hint}</button>
                            ))}
                        </div>

                        {status === 'error' && (
                            <Feedback tone="error" title="Theme could not be generated">Try again, or choose a preset theme above.</Feedback>
                        )}
                    </>
                ) : appliedTheme && (
                    <div>
                        {/* Applied theme preview */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                            <div role="status" style={{ ...mono, color: 'var(--text-primary, var(--bs-ink))', fontSize: '13px' }}>
                                Theme applied: <strong>{appliedTheme.name || 'custom'}</strong>
                            </div>
                            {/* Colour swatches */}
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                {[appliedTheme.primary, appliedTheme.secondary, appliedTheme.bg, appliedTheme.surface].filter(Boolean).map((c, i) => (
                                    <span key={i} title={c} style={{
                                        display: 'inline-block', width: 16, height: 16,
                                        borderRadius: '50%', background: c,
                                        border: '1.5px solid rgba(255,255,255,0.15)',
                                    }} />
                                ))}
                            </div>
                        </div>

                        {appliedTheme.heroText && (
                            <div style={{
                                ...mono,
                                color: 'var(--persona-primary, var(--bs-accent))',
                                fontSize: '15px',
                                marginBottom: '16px',
                                fontStyle: 'italic',
                                borderLeft: '1px solid var(--persona-primary, var(--bs-accent))',
                                paddingLeft: '12px',
                            }}>
                                "{appliedTheme.heroText}"
                            </div>
                        )}

                        <div style={{ ...mono, color: 'var(--text-muted, var(--bs-muted))', fontSize: '12px', marginBottom: '16px', lineHeight: 1.6 }}>
                            # navigate to HOME to see full effect · your theme persists across pages
                        </div>

                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            <button type="button" onClick={reset} style={{
                                ...mono, fontSize: '12px',
                                background: 'none', border: '1px solid var(--border, var(--bs-rule))',
                                borderRadius: '0', padding: '6px 14px',
                                color: 'var(--text-primary, var(--bs-ink))', cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                            >
                                $ reset-theme
                            </button>
                            <button type="button" onClick={() => { setStatus('idle'); setDescription(''); setAppliedTheme(null); }} style={{
                                ...mono, fontSize: '12px',
                                background: 'none',
                                border: '1px solid var(--border, var(--bs-rule))',
                                borderRadius: '0', padding: '6px 14px',
                                color: 'var(--text-primary, var(--bs-ink))', cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}>
                                $ try another →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Markdown components ───────────────────────────────────────────────────
// Keep article rendering rules together; the persona piece adds its preset widget after its heading.
function makeMdComponents(isPersonaArticle: boolean, onPersonaApply: (theme: PersonaTheme, id: string) => void, activePersonaId: string | null, returnPath: string): Components {
    return {
        h2: ({ children, node }) => {
            const text = typeof children === 'string' ? children : Array.isArray(children) ? children.join('') : '';
            const isPersonasSection = isPersonaArticle && text.toLowerCase().includes('persona');
            return (
                <>
                    <h2 id={`reading-${node?.position?.start.line}`} tabIndex={-1}>
                        {children}
                    </h2>
                    {isPersonasSection && (
                        <PersonaCards onApply={onPersonaApply} activeId={activePersonaId} />
                    )}
                </>
            );
        },
        h3: ({ children, node }) => (
            <h3 id={`reading-${node?.position?.start.line}`} tabIndex={-1}>
                {children}
            </h3>
        ),
        a: ({ href = '', children }) => {
            const route = href.replace(/^\/?#(?=\/)/, '');
            return route.startsWith('/') && !route.startsWith('//')
                ? <Link to={route.startsWith('/article/') ? withArticleReturn(route, returnPath) : route}>{children}</Link>
                : <a href={href}>{children}</a>;
        },
        pre: ({ children }) => {
            const child = React.Children.toArray(children)[0];
            const language = React.isValidElement<{ className?: string }>(child)
                ? child.props.className?.match(/language-([\w+-]+)/)?.[1]
                : undefined;
            const code = <pre className="article-code" tabIndex={0} aria-label={language ? `${language} code example` : 'Code or formula'}>{children}</pre>;
            return language ? <figure className="reader-code-example"><figcaption>{language}</figcaption>{code}</figure> : code;
        },
        table: ({ children }) => (
            <div className="article-table" role="region" aria-label="Article table" tabIndex={0}>
                <table>{children}</table>
            </div>
        ),
    };
}

// ─── Article page ──────────────────────────────────────────────────────────
const ArticlePage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const returnPath = articleReturnPath(searchParams);
    const article = allArticles.find(a => a.slug === slug);
    const isPersonaArticle = slug === 'persona-driven-ui';

    const [activePersonaId, setActivePersonaId] = useState<string | null>(null);
    const [liveHeroText, setLiveHeroText] = useState('');
    const [themeFlash, setThemeFlash] = useState(false);
    const [coverFailed, setCoverFailed] = useState(false);
    const proseRef = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();
    const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

    useEffect(() => {
        setCoverFailed(false);
        document.title = article
            ? `${article.title} — Abhijith Eanuga`
            : 'Article not found — Abhijith Eanuga';
    }, [article, slug]);

    // Listen for persona-applied events — catches both preset cards and custom LLM themes
    useEffect(() => {
        const handler = (e: Event) => {
            const { heroText, name } = (e as CustomEvent).detail;
            setLiveHeroText(heroText || '');
            if (!heroText && !name) setActivePersonaId(null); // reset
        };
        window.addEventListener('persona-applied', handler);
        return () => window.removeEventListener('persona-applied', handler);
    }, []);

    const handlePersonaApply = useCallback((theme: PersonaTheme, id: string) => {
        if (id === '__reset__') {
            resetPersonaTheme();
            setActivePersonaId(null);
        } else {
            applyPersonaTheme(theme);
            setActivePersonaId(id);
            // Brief flash to signal the page just transformed
            if (!reduced) {
                setThemeFlash(true);
                if (flashTimer.current) clearTimeout(flashTimer.current);
                flashTimer.current = setTimeout(() => setThemeFlash(false), 450);
            }
        }
    }, [reduced]);

    const mdComponents = useMemo(() => makeMdComponents(isPersonaArticle, handlePersonaApply, activePersonaId, returnPath),
        [isPersonaArticle, handlePersonaApply, activePersonaId, returnPath]);

    if (!article) {
        return (
            <div className="bs-page article-detail">
                <PageIntro title="Article not found" description="This piece is unavailable. Browse the writing to find another." />
                <main id="main" tabIndex={-1} className="bs-shell article-missing">
                    <Feedback action={<Link to={returnPath} className="bs-btn">Browse all writing</Link>}>
                        No article matches “{slug}”.
                    </Feedback>
                </main>
            </div>
        );
    }

    const readingMinutes = estimateReadingMinutes(article.content);
    const remaining = allArticles.filter(candidate => candidate.slug !== article.slug);
    const nextArticles = [
        ...remaining.filter(candidate => candidate.category === article.category),
        ...remaining.filter(candidate => candidate.category !== article.category),
    ].slice(0, 2);

    return (
        <div
            className={`bs-page page-enter article-detail${themeFlash ? ' persona-theme-flash' : ''}`}
            style={{
                minHeight: '100vh',
                backgroundColor: 'var(--bg, var(--bs-ground))',
                fontFamily: 'var(--bs-sans)',
            }}
        >
            <nav className="article-top-nav" aria-label="Article navigation">
                <Link to={returnPath}><ArrowLeft size={14} /> All writing</Link>
            </nav>
            <PageIntro className="article-reader-intro" title={article.title} description={article.summary}>
                <div className="article-reading-meta">
                    <span className="article-reading-category">{article.category}</span>
                    <span>{article.date}</span>
                    <span>{article.author}</span>
                    <span aria-label={`Estimated reading time: ${readingMinutes} minutes`}>{readingMinutes} min read</span>
                </div>
                {isPersonaArticle && liveHeroText && <p className="article-persona-tagline" role="status">{liveHeroText}</p>}
                {isPersonaArticle && (activePersonaId || liveHeroText) && (
                    <p className="article-persona-tagline">Theme: {PERSONAS.find(p => p.id === activePersonaId)?.label || 'custom'}</p>
                )}
            </PageIntro>

            {/* Cover image */}
            {article.cover && !coverFailed && (
                <figure className="article-cover">
                    <img
                        src={article.cover}
                        alt=""
                        width={1200}
                        height={675}
                        decoding="async"
                        onError={() => setCoverFailed(true)}
                    />
                </figure>
            )}

            {/* Article content */}
            <main id="main" tabIndex={-1} className="article-reader-layout">
                <ArticleReaderNav sourceRef={proseRef} articleKey={article.slug} renderVersion={activePersonaId} />
                <div className="reader-body">
                    <div className="article-prose" ref={proseRef}>
                        <ReactMarkdown components={mdComponents} remarkPlugins={[remarkGfm]}>
                            {article.content}
                        </ReactMarkdown>
                    </div>

                    {article.slug === 'agentic-ai-salesforce' && <AgentSystemFilm inline />}
                    <MotionStudy articleSlug={article.slug} />

                    {/* Generic demo slot */}
                    {article.demo && DEMOS[article.demo] && (() => {
                        const DemoComponent = DEMOS[article.demo!];
                        return (
                            <Suspense fallback={
                                <Feedback>Loading interactive demo…</Feedback>
                            }>
                                <DemoComponent />
                            </Suspense>
                        );
                    })()}

                    {/* Persona experiment widget */}
                    {isPersonaArticle && (
                        <>
                            <div style={{
                                marginTop: '48px', paddingTop: '48px',
                                borderTop: '1px solid var(--border, var(--bs-rule))',
                            }}>
                                <h2 style={{
                                    fontFamily: 'var(--bs-serif)', fontStyle: 'italic',
                                    color: 'var(--text-primary, var(--bs-ink))', fontSize: '32px',
                                    fontWeight: '400', marginBottom: '8px',
                                }}>
                                    Try It Live
                                </h2>
                                <p style={{ color: 'var(--text-muted, var(--bs-muted))', fontSize: '15px', marginBottom: '0' }}>
                                    Describe yourself in one sentence — the LLM generates and applies a bespoke theme in real time.
                                </p>
                            </div>
                            <PersonaExperiment onApply={handlePersonaApply} activePersonaId={activePersonaId} />
                        </>
                    )}

                    {nextArticles.length > 0 && (
                        <section className="reader-continue" aria-labelledby="reader-continue-title">
                            <h2 id="reader-continue-title">Continue reading</h2>
                            {nextArticles.map(next => (
                                <Link key={next.slug} to={withArticleReturn(`/article/${next.slug}`, returnPath)} className="reader-next-row">
                                    <h3>{next.title}</h3>
                                    <p>{next.summary}</p>
                                    <span>{next.category} · {estimateReadingMinutes(next.content)} min read</span>
                                </Link>
                            ))}
                        </section>
                    )}

                    {/* Back link */}
                    <footer className="article-reading-footer">
                        <Link to={returnPath}><ArrowLeft size={14} /> Back to all writing</Link>
                    </footer>
                </div>
            </main>
        </div>
    );
};

export default ArticlePage;
